import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EntityType } from '../../types';
import { bulkRestore, ENTITY_LABEL } from '../../api/softDelete';
import { vermieterApi } from '../../api/vermieter';
import { adressenApi } from '../../api/adressen';
import { haeuserApi } from '../../api/haeuser';
import { einheitenApi } from '../../api/einheiten';
import { verfahrenApi } from '../../api/verfahren';
import { anfragenApi } from '../../api/anfragen';
import { listingsApi } from '../../api/listings';
import { bebauungsplaeneApi } from '../../api/bebauungsplaene';
import { GEBIETSART_LABELS } from '../../types/bebauungsplan';

interface ArchiveRow {
  id: number;
  label: string;
  sublabel?: string;
  deleted_at: string | null;
  deleted_by_user_id: number | null;
  deleted_reason: string | null;
}

const ARCHIVE_PARAMS = { include_hidden: true, per_page: 500 } as const;

/**
 * One fetch path per entity: load with include_hidden, keep the soft-deleted
 * rows, map to a common ArchiveRow. Adding an entity = one registry entry, so
 * pagination limits etc. can't drift between tabs (see per_page below).
 */
function makeArchiveFetcher<T extends { deleted_at?: string | null }>(
  fetchItems: () => Promise<T[]>,
  toRow: (item: T) => ArchiveRow,
): () => Promise<ArchiveRow[]> {
  return async () => (await fetchItems()).filter((it) => it.deleted_at).map(toRow);
}

const ARCHIVE_FETCHERS: Record<EntityType, () => Promise<ArchiveRow[]>> = {
  inserat: makeArchiveFetcher(
    async () => (await listingsApi.getListings({ ...ARCHIVE_PARAMS })).items,
    (l) => ({
      id: l.id,
      label: l.title,
      sublabel: [l.platform, l.address || l.scraped_address].filter(Boolean).join(' · '),
      deleted_at: l.deleted_at ?? null,
      deleted_by_user_id: l.deleted_by_user_id ?? null,
      deleted_reason: l.deleted_reason ?? null,
    }),
  ),
  adresse: makeArchiveFetcher(
    async () => (await adressenApi.getAdressen({ ...ARCHIVE_PARAMS })).items,
    (a) => ({
      id: a.id,
      label: `${a.strasse} ${a.hausnummer}`,
      sublabel: [a.plz, a.stadt].filter(Boolean).join(' '),
      deleted_at: a.deleted_at ?? null,
      deleted_by_user_id: a.deleted_by_user_id ?? null,
      deleted_reason: a.deleted_reason ?? null,
    }),
  ),
  haus: makeArchiveFetcher(
    async () => (await haeuserApi.getHaeuser({ ...ARCHIVE_PARAMS })).items,
    (h) => ({
      id: h.id,
      label: h.bezeichnung || `Haus #${h.id}`,
      sublabel: `${h.adresse_str}${h.typ ? ' · ' + h.typ : ''}`,
      deleted_at: h.deleted_at ?? null,
      deleted_by_user_id: h.deleted_by_user_id ?? null,
      deleted_reason: h.deleted_reason ?? null,
    }),
  ),
  einheit: makeArchiveFetcher(
    async () => (await einheitenApi.getEinheiten({ ...ARCHIVE_PARAMS })).items,
    (e) => ({
      id: e.id,
      label: e.bezeichnung,
      sublabel: `${e.haus_bezeichnung} · ${e.adresse_str}`,
      deleted_at: e.deleted_at ?? null,
      deleted_by_user_id: e.deleted_by_user_id ?? null,
      deleted_reason: e.deleted_reason ?? null,
    }),
  ),
  vermieter: makeArchiveFetcher(
    async () => (await vermieterApi.getVermieter({ ...ARCHIVE_PARAMS })).items,
    (v) => ({
      id: v.id,
      label: v.name,
      sublabel: [v.typ, v.email].filter(Boolean).join(' · '),
      deleted_at: v.deleted_at ?? null,
      deleted_by_user_id: v.deleted_by_user_id ?? null,
      deleted_reason: v.deleted_reason ?? null,
    }),
  ),
  verfahren: makeArchiveFetcher(
    async () => (await verfahrenApi.getVerfahren({ ...ARCHIVE_PARAMS })).items,
    (v) => ({
      id: v.id,
      label: v.aktenzeichen || `Verfahren #${v.id}`,
      sublabel: [v.staat, v.vermieter_name].filter(Boolean).join(' · '),
      deleted_at: v.deleted_at ?? null,
      deleted_by_user_id: v.deleted_by_user_id ?? null,
      deleted_reason: v.deleted_reason ?? null,
    }),
  ),
  anfrage: makeArchiveFetcher(
    async () => (await anfragenApi.getAnfragen({ ...ARCHIVE_PARAMS })).items,
    (a) => ({
      id: a.id,
      label: a.adresse_text || `Anfrage #${a.id}`,
      sublabel: [a.status, a.zipcode, a.city].filter(Boolean).join(' · '),
      deleted_at: a.deleted_at ?? null,
      deleted_by_user_id: a.deleted_by_user_id ?? null,
      deleted_reason: a.deleted_reason ?? null,
    }),
  ),
  bebauungsplan: makeArchiveFetcher(
    async () => (await bebauungsplaeneApi.list({ ...ARCHIVE_PARAMS })).items,
    (b) => ({
      id: b.id,
      label: b.name,
      sublabel: [b.plan_nr, GEBIETSART_LABELS[b.gebietsart] ?? b.gebietsart]
        .filter(Boolean)
        .join(' · '),
      deleted_at: b.deleted_at ?? null,
      deleted_by_user_id: null,
      deleted_reason: null,
    }),
  ),
};

// System-erkannte Duplikate — eigene, read-only Kategorie (nicht Teil des
// manuellen Archivs). Zeigt den Verweis auf den beibehaltenen Datensatz.
async function fetchDuplicates(): Promise<ArchiveRow[]> {
  const items = (await listingsApi.getListings({ duplicates_only: true, per_page: 500 })).items;
  return items.map((l) => ({
    id: l.id,
    label: l.title,
    sublabel: `${[l.platform, l.zipcode || l.scraped_zipcode].filter(Boolean).join(' · ')} → Original #${l.duplicate_of_id}`,
    deleted_at: l.deleted_at ?? null,
    deleted_by_user_id: l.deleted_by_user_id ?? null,
    deleted_reason: l.deleted_reason ?? null,
  }));
}

type Tab = EntityType | 'duplikat';

function fetchArchive(tab: Tab): Promise<ArchiveRow[]> {
  if (tab === 'duplikat') return fetchDuplicates();
  return ARCHIVE_FETCHERS[tab]();
}

function tabLabelPlural(tab: Tab): string {
  return tab === 'duplikat' ? 'Duplikate' : ENTITY_LABEL[tab].plural;
}
function tabLabelSingular(tab: Tab): string {
  return tab === 'duplikat' ? 'Duplikat' : ENTITY_LABEL[tab].singular;
}

const TABS: Tab[] = [
  'inserat',
  'adresse',
  'haus',
  'einheit',
  'vermieter',
  'verfahren',
  'anfrage',
  'bebauungsplan',
  'duplikat',
];

export default function ArchivePage() {
  const [active, setActive] = useState<Tab>('inserat');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const queryClient = useQueryClient();
  const isDuplikat = active === 'duplikat';

  const { data, isLoading, error } = useQuery({
    queryKey: ['archive', active],
    queryFn: () => fetchArchive(active),
  });

  const restoreMutation = useMutation({
    mutationFn: (ids: number[]) => bulkRestore(active as EntityType, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['archive', active] });
      // Also refresh the relevant entity list page caches.
      queryClient.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && typeof q.queryKey[0] === 'string' });
      setSelected(new Set());
    },
  });

  const rows = data ?? [];
  const allSelected = rows.length > 0 && selected.size === rows.length;

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onRestore = (ids: number[]) => {
    if (ids.length === 0) return;
    restoreMutation.mutate(ids);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-semibold mb-1">Archiv</h1>
      <p className="text-sm text-slate-600 mb-6">
        Hier findest du alle ausgeblendeten Items deiner Stadt. Wiederhergestellte Items
        erscheinen wieder in den regulären Listen.
      </p>

      <div className="border-b border-slate-200 mb-4 flex flex-wrap gap-1">
        {TABS.map((entity) => (
          <button
            key={entity}
            type="button"
            onClick={() => {
              setActive(entity);
              setSelected(new Set());
            }}
            className={`px-3 py-2 text-sm font-medium border-b-2 ${
              active === entity
                ? 'border-amber-600 text-amber-700'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            {tabLabelPlural(entity)}
          </button>
        ))}
      </div>

      {isDuplikat && (
        <p className="text-sm text-slate-600 mb-3 bg-slate-50 border border-slate-200 rounded px-3 py-2">
          Automatisch erkannte Duplikate (gleiche Plattform-ID wie ein beibehaltener Datensatz).
          Sie sind aus allen Arbeitsansichten ausgeschlossen und werden nicht neu angelegt.
        </p>
      )}

      {!isDuplikat && selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between bg-amber-50 border border-amber-200 rounded px-3 py-2">
          <span className="text-sm text-amber-900">
            {selected.size} ausgewählt
          </span>
          <button
            type="button"
            onClick={() => onRestore([...selected])}
            disabled={restoreMutation.isPending}
            className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
          >
            {restoreMutation.isPending ? 'Wird wiederhergestellt…' : 'Wiederherstellen'}
          </button>
        </div>
      )}

      {isLoading && <div className="text-slate-500">Lädt…</div>}
      {error && (
        <div className="text-red-600 text-sm">
          Fehler beim Laden: {(error as Error).message}
        </div>
      )}
      {!isLoading && rows.length === 0 && (
        <div className="text-slate-500 text-sm py-8 text-center border border-dashed border-slate-300 rounded">
          {isDuplikat ? 'Keine Duplikate.' : `Keine ausgeblendeten ${tabLabelPlural(active)}.`}
        </div>
      )}

      {rows.length > 0 && (
        <table className="w-full text-sm bg-white border border-slate-200 rounded">
          <thead className="bg-slate-50 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left w-10">
                {!isDuplikat && <input type="checkbox" checked={allSelected} onChange={toggleAll} />}
              </th>
              <th className="px-3 py-2 text-left">{tabLabelSingular(active)}</th>
              <th className="px-3 py-2 text-left">{isDuplikat ? 'Erkannt am' : 'Ausgeblendet am'}</th>
              <th className="px-3 py-2 text-left">Grund</th>
              <th className="px-3 py-2 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2">
                  {!isDuplikat && (
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={() => toggleOne(row.id)}
                    />
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">{row.label}</div>
                  {row.sublabel && (
                    <div className="text-xs text-slate-500">{row.sublabel}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                  {row.deleted_at ? new Date(row.deleted_at).toLocaleString('de-DE') : '—'}
                </td>
                <td className="px-3 py-2 text-slate-600">{row.deleted_reason || '—'}</td>
                <td className="px-3 py-2 text-right">
                  {isDuplikat ? (
                    <span className="text-slate-400 text-xs">—</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onRestore([row.id])}
                      disabled={restoreMutation.isPending}
                      className="text-green-700 hover:text-green-900 text-sm font-medium disabled:opacity-50"
                    >
                      Wiederherstellen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
